/** `count`: the key of the sidebar badge in the nav counters (see `useNavCounts`). */
export type NavItem = { to: string; label: string; icon: string; count?: string };
/** `gate`: shown only when the account has that product switched on. */
export type NavSection = { title: string; items: NavItem[]; gate?: 'vps' };

export const NAV: NavSection[] = [
  {
    title: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', icon: 'nav-dashboard' },
      { to: '/tickets', label: 'Support Tickets', icon: 'nav-tickets', count: 'tickets' },
      { to: '/limits', label: 'Service Limits', icon: 'nav-limits' },
    ],
  },
  {
    title: 'Regular Infrastructure',
    gate: 'vps',
    items: [{ to: '/vps', label: 'Simple VPS', icon: 'nav-vps', count: 'vps' }],
  },
  {
    title: 'Cloud Infrastructure',
    items: [
      { to: '/vms', label: 'Virtual Machines', icon: 'nav-vm', count: 'vms' },
      { to: '/volumes', label: 'Volumes', icon: 'nav-volumes', count: 'volumes' },
      { to: '/ssh-keys', label: 'SSH Keys', icon: 'nav-keys', count: 'ssh_keys' },
      { to: '/backups', label: 'Backups', icon: 'nav-backups', count: 'backups' },
      { to: '/snapshots', label: 'Snapshots', icon: 'nav-snapshots', count: 'snapshots' },
      { to: '/isos', label: 'ISO catalog', icon: 'nav-iso', count: 'isos' },
    ],
  },
  {
    title: 'Cloud Network',
    items: [
      { to: '/routers', label: 'Routers', icon: 'nav-routers', count: 'routers' },
      { to: '/networks', label: 'Networks', icon: 'nav-networks', count: 'networks' },
      { to: '/vpn', label: 'VPN Gateway', icon: 'nav-vpn', count: 'vpn_devices' },
      { to: '/private-dns', label: 'Private DNS', icon: 'nav-dns', count: 'dns_zones' },
      { to: '/public-dns', label: 'Public DNS', icon: 'nav-pdns' },
      { to: '/floating-ips', label: 'Floating IPs', icon: 'nav-fip', count: 'floating_ips' },
      { to: '/load-balancers', label: 'Load Balancers', icon: 'nav-lb', count: 'balancers' },
      { to: '/security-groups', label: 'Security Groups', icon: 'nav-sg', count: 'security_groups' },
    ],
  },
  {
    title: 'Managed Services',
    items: [
      { to: '/databases', label: 'Databases & Cache', icon: 'nav-db', count: 'dbaas' },
      { to: '/brokers', label: 'Message Brokers', icon: 'nav-mq', count: 'brokers' },
      { to: '/apps', label: 'One-Click Apps', icon: 'nav-apps', count: 'apps' },
      { to: '/kubernetes', label: 'Kubernetes', icon: 'nav-k8s', count: 'k8s_clusters' },
      { to: '/storage', label: 'Storage Boxes', icon: 'nav-storage', count: 'storage_boxes' },
      { to: '/registry', label: 'Container Registry', icon: 'nav-registry', count: 'registries' },
      { to: '/vault', label: 'Secrets Vault', icon: 'nav-vault', count: 'vaults' },
      { to: '/email', label: 'Email Gateway', icon: 'nav-mail' },
      { to: '/monitoring', label: 'Monitoring', icon: 'nav-monitoring' },
    ],
  },
];

export const NAV_ITEMS = NAV.flatMap((s) => s.items);

/** Pages reached from the top bar, not the sidebar: they still need a name in the crumbs. */
const OFF_NAV = [
  { to: '/notifications', label: 'Notifications' },
  { to: '/billing', label: 'Billing' },
  { to: '/settings', label: 'Account' },
];

/** The section a path belongs to, for the crumbs. */
export function sectionOf(path: string): { to: string; label: string } | null {
  return [...NAV_ITEMS, ...OFF_NAV].find((i) => (i.to === '/' ? path === '/' : path === i.to || path.startsWith(i.to + '/'))) ?? null;
}
